import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
} from '../../../settings/store/services/details';
import { EditPSPBrandFormValues, PSPBrandKeys } from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';

const useBrand = () => {
  const {
    settings: {
      details: { validators, pspDetails },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPBrandFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
    setValue,
  } = useForm<EditPSPBrandFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty =
    !isEqual(fields, defaultFormValues) || Boolean(Object.keys(errors).length);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPBrandKeys;
    }[] = [];

    const brandColors = pspDetails?.find((item) => item.key === 'WHITELABEL');

    if (brandColors) {
      const data = brandColors.data.value as Record<string, string>;

      updatedSettings.push({
        whitelabel: {
          baseFeedbackError: data.baseFeedbackError,
          baseNeutral0: data.baseNeutral0,
          baseNeutral1: data.baseNeutral2,
          baseNeutral2: data.baseNeutral2,
          baseNeutral3: data.baseNeutral3,
          baseNeutral4: data.baseNeutral4,
          baseNeutral5: data.baseNeutral5,
          baseNeutral6: data.baseNeutral6,
          baseNeutral7: data.baseNeutral7,
          baseNeutral8: data.baseNeutral8,
          baseNeutral9: data.baseNeutral9,
          baseNeutral10: data.baseNeutral10,
          baseSecondaryGreen1: data.baseSecondaryGreen1,
          brandPrimary: values.brandColor,
          cardLogo: values.cardLogo,
          companyName: values.companyName,
          contextBackground: data.contextBackground,
          favicon: values.favicon,
          headerLogo: values.headerLogo,
          loaderIcon: values.loaderIcon,
        },
        key: PSPBrandKeys.WHITELABEL,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));
      }

      setDefaultFormValues({
        brandColor: values?.brandColor || '',
        companyName: values?.companyName || '',
        cardLogo: values?.cardLogo || '',
        favicon: values?.favicon || '',
        headerLogo: values?.headerLogo || '',
        loaderIcon: values?.loaderIcon || '',
      });
    }
  });

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const brandColors = pspDetails?.find((item) => item.key === 'WHITELABEL');

      reset({
        brandColor:
          (brandColors?.data?.value as Record<string, string>)?.brandPrimary ||
          '',
        companyName:
          (brandColors?.data?.value as Record<string, string>)?.companyName ||
          '',
        cardLogo:
          (brandColors?.data?.value as Record<string, string>)?.cardLogo || '',
        headerLogo:
          (brandColors?.data?.value as Record<string, string>)?.headerLogo ||
          '',
        favicon:
          (brandColors?.data?.value as Record<string, string>)?.favicon || '',
        loaderIcon:
          (brandColors?.data?.value as Record<string, string>)?.loaderIcon ||
          '',
      });

      setDefaultFormValues({
        brandColor:
          (brandColors?.data?.value as Record<string, string>)?.brandPrimary ||
          '',
        companyName:
          (brandColors?.data?.value as Record<string, string>)?.companyName ||
          '',
        cardLogo:
          (brandColors?.data?.value as Record<string, string>)?.cardLogo || '',
        headerLogo:
          (brandColors?.data?.value as Record<string, string>)?.headerLogo ||
          '',
        favicon:
          (brandColors?.data?.value as Record<string, string>)?.favicon || '',
        loaderIcon:
          (brandColors?.data?.value as Record<string, string>)?.loaderIcon ||
          '',
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  return {
    isLoading,
    errorList,
    control,
    errors,
    onSubmit,
    fields,
    globalError,
    editIsLoading,
    isDirty,
    setValue,
  };
};

export default useBrand;
